/**
 * The task lists the "About this page" dialog and the Settings → Help guide
 * share: which tasks a reader may see, the search box, search results and a
 * single expandable task.
 */
import { useNavigate } from "react-router-dom";
import { ArrowRight, Crosshair, Loader2, Search, X } from "lucide-react";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { fill } from "@/lib/i18n/fill";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { PageInfoId, PageInfoItemCopy } from "@/lib/i18n/types/pageInfo";
import { findPageInfoTarget } from "./highlightPageInfoTarget";
import { requestPageInfoOpen } from "./pendingPageInfoOpen";
import { itemDef, type PageInfoScope } from "./visibleChunks";
import { PAGE_INFO } from "./pageInfoRegistry";
import { PAGE_INFO_MAX_QUERY_CHARS } from "./search/embedConfig";
import type { PageInfoChunk } from "./search/pageInfoChunks";
import type { usePageInfoSearch } from "./search/usePageInfoSearch";

export function PageInfoSearchBox({
  query,
  onQueryChange,
  className,
}: {
  query: string;
  onQueryChange: (query: string) => void;
  className?: string;
}) {
  const { t } = useLanguage();
  return (
    <div className={["relative", className].filter(Boolean).join(" ")}>
      <Search
        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
        aria-hidden="true"
      />
      <Input
        type="search"
        value={query}
        maxLength={PAGE_INFO_MAX_QUERY_CHARS}
        onChange={(event) => onQueryChange(event.target.value)}
        placeholder={t.pageInfo.search.placeholder}
        aria-label={t.pageInfo.search.label}
        className="h-10 pl-9 pr-9 [&::-webkit-search-cancel-button]:hidden"
      />
      {query && (
        <button
          type="button"
          onClick={() => onQueryChange("")}
          aria-label={t.pageInfo.search.clear}
          className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

/** This page's hits first; within each group the search's own order stands. */
function thisPageFirst<T extends { chunk: PageInfoChunk }>(
  hits: T[],
  page: PageInfoId | null
): T[] {
  if (page === null) return hits;
  return [
    ...hits.filter((hit) => hit.chunk.page === page),
    ...hits.filter((hit) => hit.chunk.page !== page),
  ];
}

interface SearchResultsProps {
  /** The page the results are shown on; null outside any page (the Help guide). */
  page: PageInfoId | null;
  scope: PageInfoScope;
  search: ReturnType<typeof usePageInfoSearch>;
  onShowMe?: (target: string) => void;
  onLeave?: () => void;
}

export function SearchResults({ page, scope, search, onShowMe, onLeave }: SearchResultsProps) {
  const { t } = useLanguage();
  const s = t.pageInfo.search;
  const keyword = thisPageFirst(search.keyword, page);
  const related = thisPageFirst(search.related, page);
  const settled = search.semanticStatus !== "loading";
  const total = keyword.length + related.length;

  const renderItems = (hits: Array<{ chunk: PageInfoChunk }>) =>
    hits.map(({ chunk }) => (
      <TaskItem
        key={chunk.key}
        chunk={chunk}
        page={page}
        scope={scope}
        onShowMe={onShowMe}
        onLeave={onLeave}
      />
    ));

  return (
    <div className="space-y-4 py-2">
      <p className="sr-only" aria-live="polite">
        {settled ? fill(s.resultCount, { count: String(total) }) : ""}
      </p>

      {keyword.length > 0 && (
        <section aria-label={s.matchesHeading}>
          <h3 className="pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {s.matchesHeading}
          </h3>
          <Accordion type="single" collapsible>
            {renderItems(keyword)}
          </Accordion>
        </section>
      )}

      {search.semanticStatus === "loading" && (
        <div
          role="status"
          className="flex items-start gap-3 rounded-md border border-dashed px-4 py-3 text-sm text-muted-foreground"
        >
          <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin" aria-hidden="true" />
          <div className="flex-1 space-y-2">
            <p>{s.loadingRelated}</p>
            <div className="h-2.5 w-3/4 animate-pulse rounded bg-muted motion-reduce:animate-none" />
            <div className="h-2.5 w-1/2 animate-pulse rounded bg-muted motion-reduce:animate-none" />
          </div>
        </div>
      )}

      {related.length > 0 && (
        <section aria-label={s.relatedHeading}>
          <h3 className="pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {s.relatedHeading}
          </h3>
          <Accordion type="single" collapsible>
            {renderItems(related)}
          </Accordion>
        </section>
      )}

      {search.semanticStatus === "unavailable" && (
        <p className="text-xs text-muted-foreground">{s.relatedUnavailable}</p>
      )}

      {settled && total === 0 && (
        <p className="py-8 text-center text-sm text-muted-foreground">{s.noMatches}</p>
      )}
    </div>
  );
}

interface TaskItemProps {
  chunk: PageInfoChunk;
  /** The page the list is shown on; null outside any page (the Help guide). */
  page: PageInfoId | null;
  scope: PageInfoScope;
  onShowMe?: (target: string) => void;
  /** Called before "Go to …" navigates (the dialog closes itself). */
  onLeave?: () => void;
  /** Inside a page's own card in the guide: no "On …" badge, no "Go to …". */
  inPageCard?: boolean;
}

export function TaskItem({
  chunk,
  page,
  scope,
  onShowMe,
  onLeave,
  inPageCard = false,
}: TaskItemProps) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const def = itemDef(chunk.page, chunk.item);
  const Icon = def?.icon;
  const otherPage = chunk.page !== page && !inPageCard;
  const text = (t.pageInfo.pages[chunk.page].items as Record<string, PageInfoItemCopy>)[chunk.item];
  const route = otherPage ? PAGE_INFO[chunk.page].route(scope) : null;
  // Looked up when rendered (the task is expanded), so a control that appeared
  // after the dialog opened still gets its "Show me".
  const target =
    chunk.page === page && onShowMe && def?.target && findPageInfoTarget(def.target)
      ? def.target
      : null;

  return (
    <AccordionItem value={chunk.key} className="last:border-b-0">
      <AccordionTrigger className="gap-3 rounded-md py-3 text-left text-sm hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring">
        <span className="flex min-w-0 flex-1 items-center gap-3">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted text-foreground">
            {Icon && <Icon className="h-4 w-4" aria-hidden="true" />}
          </span>
          <span className="min-w-0">{text.title}</span>
          {otherPage && (
            <span className="ml-auto shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
              {fill(t.pageInfo.search.otherPage, { page: chunk.pageTitle })}
            </span>
          )}
        </span>
      </AccordionTrigger>
      <AccordionContent className="pl-10">
        <ul className="list-disc space-y-1 pl-4 text-muted-foreground marker:text-muted-foreground/60">
          {text.points.map((point) => (
            <li key={point}>{point}</li>
          ))}
        </ul>
        {target && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-3 gap-1.5"
            aria-label={fill(t.pageInfo.showMeLabel, { task: text.title })}
            onClick={() => onShowMe?.(target)}
          >
            <Crosshair className="h-3.5 w-3.5" aria-hidden="true" />
            {t.pageInfo.showMe}
          </Button>
        )}
        {otherPage &&
          (route ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-3 gap-1.5"
              onClick={() => {
                requestPageInfoOpen(chunk.page, chunk.item);
                onLeave?.();
                navigate(route);
              }}
            >
              {fill(t.pageInfo.search.goToPage, { page: chunk.pageTitle })}
              <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
            </Button>
          ) : (
            <p className="mt-3 text-xs text-muted-foreground">
              {t.pageInfo.search.openBuildingFirst}
            </p>
          ))}
      </AccordionContent>
    </AccordionItem>
  );
}
